<template>
  <!-- The workspace does not exist, or can no longer be reached (issue 364 F57).
       It replaces the WHOLE app, not one view: the gate is on reachability, so
       every route behind it has nothing to show — the same reasoning portal-ui
       applies to a dark career portal. The login/reset/invite doors are exempt
       (meta.tenantless, see the router), because those still work. -->
  <TenantUnavailableView v-if="tenantUnavailable" :account-url="accountUrl" />
  <template v-else>
    <ToastContainer />
    <NoticeDialogHost />
    <AppChrome />
    <!-- Suite setup wizard: a modal over the workspace (issue 214). Self-gates on
         ?setup=1 / an in-flight commit for admins, so it can live at the app root. -->
    <SuiteWizardView />
    <!-- Idle session timeout (issue 265): 30 minutes without user input ends the
         session, warned a minute ahead. Shared across the suite via app-kit. -->
    <IdleLogoutGuard :authenticated="hasSession" @timeout="onIdleTimeout" />
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  ToastContainer,
  NoticeDialogHost,
  IdleLogoutGuard,
  IDLE_LOGOUT_REASON,
  TenantUnavailableView,
  tenantUnavailable,
} from '@lumi/app-kit';
import { accountBaseUrl } from '@/utils/module-links';
import AppChrome from '@/components/AppChrome.vue';
import SuiteWizardView from '@/views/SuiteWizardView.vue';
import { useAuthStore } from '@/stores/auth';

// The suite's idle sign-out (issue 265). The workspace hosts the single login,
// so its bounce is an ordinary route push carrying the reason — the module UIs
// have to cross an origin to get here. Logging out revokes the refresh token
// server-side, so the session cannot be resumed.
const authStore = useAuthStore();
const router = useRouter();
const hasSession = computed(() => Boolean(authStore.token));
// The one address that still works while this workspace does not (issue 364 F57).
// Resolved by the app, not by app-kit: app-kit owns no runtime config.
const accountUrl = accountBaseUrl();

function onIdleTimeout() {
  authStore.logout();
  void router.push({ path: '/login', query: { reason: IDLE_LOGOUT_REASON } });
}
</script>
